import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select, Toggle } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { t } from '@/i18n'

interface LabelSettings {
  prefix: string
  barcodeLength: number
  widthMm: number
  heightMm: number
  showPrice: boolean
}

/**
 * How this shop numbers the barcodes it prints itself, and what a label looks like.
 *
 * Two quite different shops use this screen. One scans everything and wants a real EAN-13, which
 * carries a check digit and is guaranteed never to collide with a manufacturer's code. The other
 * has no scanner at all and types the number in at every sale — for them thirteen digits is the
 * difference between the system being usable and not.
 */
export function LabelsSection() {
  const queryClient = useQueryClient()
  const canManage = useSession((state) => state.can)(Permission.ManageSettings)

  const [prefix, setPrefix] = useState('29')
  const [length, setLength] = useState(13)
  const [width, setWidth] = useState(50)
  const [height, setHeight] = useState(30)
  const [showPrice, setShowPrice] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const settings = useQuery({
    queryKey: ['settings', 'labels'],
    queryFn: () => apiClient.get<LabelSettings>('/api/settings/labels'),
  })

  useEffect(() => {
    if (settings.data) {
      setPrefix(settings.data.prefix)
      setLength(settings.data.barcodeLength)
      setWidth(settings.data.widthMm)
      setHeight(settings.data.heightMm)
      setShowPrice(settings.data.showPrice)
    }
  }, [settings.data])

  const save = useMutation({
    mutationFn: () =>
      apiClient.put('/api/settings/labels', {
        prefix,
        barcodeLength: length,
        widthMm: width,
        heightMm: height,
        showPrice,
      }),
    onSuccess: async () => {
      setError(null)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
      await queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.'),
  })

  const isEan13 = length === 13
  // Illustrative rather than the real next number — the shop's counter lives on the server, and
  // asking for it just to draw an example would mean a request on every keystroke.
  const example = isEan13 ? `${prefix}00000000019` : '1'.padStart(length, '0')

  return (
    <div>
      <p className="mb-4 text-sm text-[var(--text-secondary)]">
        Stock that arrives with no barcode — loose goods, a local supplier, anything repacked out
        the back — gets a code the shop prints for itself. This is how those are numbered.
      </p>

      <div className="surface-raised rounded-[var(--radius-card)] border p-4">
        <div className="flex flex-col gap-4">
          <Select
            label={t('Barcode length')}
            value={String(length)}
            disabled={!canManage}
            onChange={(event) => setLength(Number(event.target.value))}
          >
            <option value="13">13 digits — full EAN-13 (recommended for scanners)</option>
            <option value="4">4 digits — 0001, 0002…</option>
            <option value="5">5 digits</option>
            <option value="6">6 digits</option>
            <option value="7">7 digits</option>
            <option value="8">8 digits</option>
            <option value="10">10 digits</option>
            <option value="12">12 digits</option>
          </Select>

          <p className="-mt-2 text-xs text-[var(--text-secondary)]">
            {isEan13
              ? 'A real EAN-13. It carries a check digit, and cannot collide with a supplier’s barcode.'
              : 'A plain number, printed as Code 128. Far shorter to key in by hand, but with no check digit.'}
          </p>

          {/* Said outright, because it is the question every shop asks before choosing a short
              code and the answer is the reason a short code is safe to offer at all. */}
          <p className="surface-sunken tabular rounded-lg px-3 py-2 text-xs text-[var(--text-secondary)]">
            Next code would look like <span className="font-semibold">{example}</span>.{' '}
            {!isEan13 && (
              <>
                Running out is not a wall — {'9'.repeat(length)} is followed by{' '}
                {'1' + '0'.repeat(length)}, and existing labels keep working.
              </>
            )}
          </p>

          {isEan13 && (
            <Input
              label={t('In-store prefix')}
              value={prefix}
              disabled={!canManage}
              maxLength={2}
              inputMode="numeric"
              onChange={(event) => setPrefix(event.target.value)}
              hint={t('02, or 20 to 29 — the range GS1 keeps for a shop’s own use. Anything else can collide with a real product.')}
            />
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={t('Label width (mm)')}
              type="number"
              value={String(width)}
              disabled={!canManage}
              onChange={(event) => setWidth(Number(event.target.value))}
              hint={t('Match the roll in the printer')}
            />
            <Input
              label={t('Label height (mm)')}
              type="number"
              value={String(height)}
              disabled={!canManage}
              onChange={(event) => setHeight(Number(event.target.value))}
            />
          </div>

          <Toggle
            checked={showPrice}
            onChange={setShowPrice}
            label={t('Print the price on the label')}
            hint={t('Weighed goods print “/kg” beside it, since that is a different promise')}
          />

          {error && (
            <p role="alert" className="text-sm text-danger-500">
              <TriangleAlert className="mr-1 inline h-4 w-4" />
              {error}
            </p>
          )}

          {canManage && (
            <div className="flex items-center gap-3">
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
              {saved && <span className="text-sm text-success-500">{t('Saved')}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
